import React from 'react';
import { Box, Flex, Text, useBreakpointValue } from '@skin-seoul/design-system';
import ReviewImageGallery from './ReviewImageGallery';
import type { Review, ReviewImage } from '..';
import { StarIcon } from '../assets/icons/StarIcon';

interface ReviewHeaderProps {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    [key: number]: number;
  };
  onlyScore?: boolean;
  onlyDistribution?: boolean;
  customerImages?: ReviewImage[];
  reviews?: Review[];
  onViewAllImages?: () => void;
}

import type { ComponentProps } from 'react';

type BoxProps = ComponentProps<typeof Box>;
type FlexProps = ComponentProps<typeof Flex>;
type TextProps = ComponentProps<typeof Text>;

const Container = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex
    mt="24px"
    direction={{ base: 'column', tablet: 'row' }}
    wrap="nowrap"
    h={{ base: 'auto', tablet: '131px' }}
    align={{ base: 'stretch', tablet: 'flex-start' }}
    justify={{ base: 'flex-start', tablet: 'flex-start' }}
    gap={{ base: '24px', tablet: '32px' }}
    {...props}
  >
    {children}
  </Flex>
);

const TopSection = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex
    direction="row"
    justify="space-between"
    align="flex-start"
    gap={{ base: '16px', tablet: '24px' }}
    flex={{ base: 'none', tablet: 1 }}
    {...props}
  >
    {children}
  </Flex>
);

const EmptyStateContainer = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex direction="column" align="flex-start" w="full" py="24px" gap="8px" borderBottom="1px solid #e9eaeb" {...props}>
    {children}
  </Flex>
);

const EmptyStateSubtitle = ({ children, ...props }: { children: React.ReactNode } & Omit<TextProps, 'children'>) => (
  <Text
    color="#2e2e2e"
    fontFamily="Plus Jakarta Sans"
    style={{ fontSize: '14px', lineHeight: '140%' }}
    fontWeight="700"
    {...props}
  >
    {children}
  </Text>
);

const EmptyStateText = ({ children, ...props }: { children: React.ReactNode } & Omit<TextProps, 'children'>) => (
  <Text
    color="#2e2e2e"
    fontFamily="Plus Jakarta Sans"
    style={{ fontSize: '12px', fontFeatureSettings: "'ss10' on", lineHeight: '140%' }}
    fontWeight="400"
    {...props}
  >
    {children}
  </Text>
);

const RatingSummary = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex
    direction={{ base: 'column', tablet: 'row' }}
    align={{ base: 'center', tablet: 'flex-start' }}
    minW={{ base: '133px', tablet: '220px' }}
    maxW={{ base: 'none', tablet: '260px' }}
    h="fit-content"
    justify={{ base: 'center', tablet: 'flex-start' }}
    {...props}
  >
    {children}
  </Flex>
);

const RatingCircle = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex
    w="70px"
    h="70px"
    bg="#3d1690"
    borderRadius="14px"
    boxSizing="border-box"
    align="center"
    justify="center"
    color="#ffffff"
    fontFamily="Plus Jakarta Sans"
    style={{ fontSize: '30px', lineHeight: '50px' }}
    fontWeight="700"
    p={{ base: '0', tablet: '10px' }}
    mr={{ base: '0', tablet: '16px' }}
    mb={{ base: '0', tablet: '0' }}
    {...props}
  >
    {children}
  </Flex>
);

const StarBlock = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex
    direction={{ base: 'column', tablet: 'column-reverse' }}
    align={{ base: 'center', tablet: 'flex-start' }}
    gap={{ base: '22px', tablet: '16px' }}
    {...props}
  >
    {children}
  </Flex>
);

const RatingWrapper = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex
    direction="column"
    align={{ base: 'center', tablet: 'flex-start' }}
    gap={{ base: '4px', tablet: '11px' }}
    {...props}
  >
    {children}
  </Flex>
);

const ReviewCount = ({ children, ...props }: { children: React.ReactNode } & Omit<TextProps, 'children'>) => (
  <Text
    color="#6e6e6e"
    m="4px 0 0 0"
    textAlign={{ base: 'center', tablet: 'left' }}
    fontFamily="Plus Jakarta Sans"
    fontWeight="400"
    {...props}
    style={{
      fontSize: '12px',
      lineHeight: 'normal',
    }}
    css={{
      '@media (min-width: 768px)': {
        fontSize: '14px',
      },
    }}
  >
    {children}
  </Text>
);

const StarContainer = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex gap="4px" {...props}>
    {children}
  </Flex>
);

const StyledStarIcon = ({ filled, ...props }: { filled: boolean } & ComponentProps<typeof StarIcon>) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  return <StarIcon filled={filled} width={isMobile ? 26 : 32} height={isMobile ? 26 : 32} {...props} />;
};

const RatingText = ({ children, ...props }: { children: React.ReactNode } & Omit<TextProps, 'children'>) => (
  <Text
    fontWeight="500"
    color="#2e2e2e"
    m="0"
    textAlign={{ base: 'center', tablet: 'left' }}
    style={{ fontSize: '14px' }}
    {...props}
    css={{
      '@media (min-width: 768px)': {
        fontSize: '18px',
      },
    }}
  >
    {children}
  </Text>
);

const Divider = ({ ...props }: BoxProps) => (
  <Box w="1px" minW="1px" bg="#dcdcdc" h={{ base: '141px', tablet: '131px' }} flexShrink="0" {...props} />
);

const ResponsiveDivider = ({ ...props }: BoxProps) => (
  <Box
    bg="#dcdcdc"
    w={{ base: 'full', tablet: '1px' }}
    h={{ base: '1px', tablet: '131px' }}
    m="0"
    flexShrink={{ base: 'initial', tablet: '0' }}
    {...props}
  />
);

const RatingDistribution = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex direction="column" justify="space-between" h={{ base: '141px', tablet: '131px' }} minW="200px" {...props}>
    {children}
  </Flex>
);

const RatingBar = ({ children, ...props }: { children: React.ReactNode } & Omit<FlexProps, 'children'>) => (
  <Flex align="center" gap="12px" {...props}>
    {children}
  </Flex>
);

const RatingNumber = ({ children, ...props }: { children: React.ReactNode } & Omit<BoxProps, 'children'>) => (
  <Box as="span" style={{ fontSize: '14px' }} color="#2e2e2e" minW="10px" {...props}>
    {children}
  </Box>
);

const ProgressBar = ({ percentage, ...props }: { percentage: number } & Omit<BoxProps, 'children'>) => (
  <Box
    w={{ base: '75px', tablet: '127px' }}
    h="5px"
    bg="#dcdcdc"
    borderRadius="4px"
    overflow="hidden"
    position="relative"
    _after={{
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: `${percentage}%`,
      bg: '#303030',
      borderRadius: '4px',
    }}
    {...props}
  />
);

const RatingLabel = ({ children, ...props }: { children: React.ReactNode } & Omit<BoxProps, 'children'>) => (
  <Box
    as="span"
    fontFamily="Plus Jakarta Sans"
    style={{ fontSize: '14px', lineHeight: 'normal' }}
    fontWeight="400"
    {...props}
  >
    {children}
  </Box>
);

const ratingLabels: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Excellent',
  5: 'Outstanding',
};

export const ReviewHeader = ({
  averageRating,
  totalReviews,
  ratingDistribution,
  onlyScore = false,
  onlyDistribution = false,
  customerImages = [],
  reviews = [],
  onViewAllImages,
}: ReviewHeaderProps) => {
  if (totalReviews === 0) {
    return (
      <EmptyStateContainer>
        <EmptyStateSubtitle>Be the first to write a review!</EmptyStateSubtitle>
        <EmptyStateText>Tell others what you think!</EmptyStateText>
      </EmptyStateContainer>
    );
  }

  if (onlyScore) {
    return (
      <Container>
        <RatingSummary>
          <RatingCircle>{averageRating.toFixed(1)}</RatingCircle>
          <StarBlock>
            <RatingWrapper>
              <StarContainer>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StyledStarIcon key={star} filled={star <= Math.round(averageRating)} />
                ))}
              </StarContainer>
              <RatingWrapper>
                <RatingText>{ratingLabels[Math.ceil(averageRating)]}</RatingText>
                <ReviewCount>
                  Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
                </ReviewCount>
              </RatingWrapper>
            </RatingWrapper>
          </StarBlock>
        </RatingSummary>
      </Container>
    );
  }

  const getDistributionPercentage = (rating: number): number => {
    const count = ratingDistribution[rating] || 0;
    return (count / totalReviews) * 100;
  };

  return (
    <Container>
      <TopSection>
        <RatingSummary>
          <RatingCircle>{averageRating.toFixed(1)}</RatingCircle>
          <StarBlock>
            <StarContainer>
              {[1, 2, 3, 4, 5].map((star) => (
                <StyledStarIcon key={star} filled={star <= Math.round(averageRating)} />
              ))}
            </StarContainer>
            <RatingWrapper>
              <RatingText>{ratingLabels[Math.ceil(averageRating)]}</RatingText>
              <ReviewCount>
                Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
              </ReviewCount>
            </RatingWrapper>
          </StarBlock>
        </RatingSummary>

        {!onlyDistribution && <Divider />}

        {!onlyDistribution && (
          <RatingDistribution>
            {Object.entries(ratingDistribution)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([rating]) => (
                <RatingBar key={rating}>
                  <RatingNumber>{rating}</RatingNumber>
                  <ProgressBar percentage={getDistributionPercentage(Number(rating))} />
                  <RatingLabel>{ratingLabels[Number(rating)]}</RatingLabel>
                </RatingBar>
              ))}
          </RatingDistribution>
        )}
      </TopSection>
      {!onlyDistribution && <ResponsiveDivider />}
      {reviews && reviews.length > 0 && (
        <Box flex="1" minW="0">
          <ReviewImageGallery images={customerImages} onViewAll={onViewAllImages} />
        </Box>
      )}
    </Container>
  );
};

export default ReviewHeader;
